import { object, string } from "yup";
import { get } from "lodash";
import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";

import { getDaysInMonth, isValid } from "date-fns";

import { getAge, invertDate } from "../../utils/testFns";
import {
  setCurrentStepRegistrationAction,
  setLastStepRegistrationAction,
  setPassThroughAddressAction,
  setWelcomeFieldsRegistrationAction,
  setWelcomeValidFormRegistrationAction
} from "../../actions";
import { handleSubmitCatch } from "../../utils/handlers";
import {
  onClickContinueBtnGtmEvent,
  onFormStepSuccessGtmEvent
} from "../../utils/gtm/formCompletion";

const MAX_AGE = 130;
const LEGAL_AGE = 18;

const validateDay = ({ month, day, year }) => {
  const lastDay = getDaysInMonth(new Date(+year, +month - 1));
  const validDay = day > lastDay ? lastDay : day;
  return validDay;
};

export const defaultListItems = {
  capital: {
    status: "NORMAL",
    text: "At least 1 capital letter"
  },
  number: {
    status: "NORMAL",
    text: "At least 1 number"
  },
  chars: {
    status: "NORMAL",
    text: "Minimum of 10 characters"
  }
};

export const nonCharacterRegex =
  /^[ a-zA-ZàèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ'`'-]+$/;

export const defaultRegistrationTextsCapiMessage = {
  welcome: {
    errors: {
      emptyFirstName: "",
      emptyLastName: "",
      birthUnder18: "",
      emptyOrInvalidBirth: "",
      validEmail: "",
      emptyEmail: "",
      incompletePhone: "",
      emptyPhone: "",
      requirementsNotMet: "",
      emptyPassword: ""
    },
    mobileRedirectText: "",
    redirectText: "",
    pageTitle: "",
    pageDescription: "",
    innerTitle: "",
    innerDescription: "",
    promocode: {
      pageTitle: ""
    },
    referralcode: {
      pageTitle: ""
    },
    promoCTA: "",
    notFoundPromo: ""
  },
  address: {
    home: {},
    mailing: {}
  },
  modals: {
    tvPreferences: {
      modalTitle: "",
      title: "",
      description: "",
      selectDefaultValue: "",
      buttonText: "",
      defaultProviders: [
        { value: "04864", key: "Apple TV" },
        { value: "04862", key: "Amazon Fire Stick" },
        { value: "04863", key: "Roku" },
        { value: "tvProvider", key: "TV Provider" }
      ]
    },
    promoCode: {
      modalTitle: "Promo code",
      firstTitle: "Enter Promo Code",
      secondTitle: "Available promotions",
      buttonText: "Continue",
      error: "Invalid promo code"
    },
    legalContent: {
      privacyPolicy: "",
      termsAndConditions: ""
    }
  },
  review: {
    innerTitle: "",
    innerDescription: "",
    passwordText: "",
    legalContent: undefined,
    termsAndConditions: "",
    gamblingProblem: "",
    stateNotes: undefined
  },
  onfido: {
    statesPassportReq: []
  },
  success: {
    headerTitle: "",
    headerDescription: "",
    info: {
      default: {
        show: false
      },
      promo: {
        show: false
      },
      raf: {
        show: false
      }
    }
  },
  ssn: {
    errors: {
      invalidSequence: "",
      incomplete: "",
      empty: ""
    },
    innerTitle: "",
    innerDescriptionTitle: "",
    innerDescriptionTitle2: "",
    innerDescription: ["", "", ""],
    innerDescription2: ["", "", ""],
    inputLabel: "",
    msgBoxTitle: ""
  },
  fullSsn: {
    errors: {
      invalidSequence: "",
      incomplete: "",
      empty: ""
    },
    innerTitle: "",
    innerDescriptionTitle: "",
    innerDescription: ["", "", ""],
    inputLabel: "",
    msgBoxTitle: "",
    msgBoxDesc: ["", ""]
  }
};

export const handlePasswordChange = (
  value,
  capitalListItem,
  setCapitalListItem,
  numberListItem,
  setNumberListItem,
  charsListItem,
  setCharsListItem,
  checkWarning
) => {
  if (/.*[A-Z].*/.test(value)) {
    setCapitalListItem({
      ...capitalListItem,
      status: "SUCCESS"
    });
  } else if (checkWarning) {
    setCapitalListItem({
      ...capitalListItem,
      status: "WARNING"
    });
  } else {
    setCapitalListItem(defaultListItems.capital);
  }

  if (/\d/.test(value)) {
    setNumberListItem({
      ...numberListItem,
      status: "SUCCESS"
    });
  } else if (checkWarning) {
    setNumberListItem({
      ...numberListItem,
      status: "WARNING"
    });
  } else {
    setNumberListItem(defaultListItems.number);
  }

  if (get(value, "length", 0) >= 10) {
    setCharsListItem({
      ...charsListItem,
      status: "SUCCESS"
    });
  } else if (checkWarning) {
    setCharsListItem({
      ...charsListItem,
      status: "WARNING"
    });
  } else {
    setCharsListItem(defaultListItems.chars);
  }
};

export const firstNameSchema = ({ registrationTexts }) =>
  string()
    .matches(nonCharacterRegex, {
      message: registrationTexts.welcome.errors.illegalCharacter,
      excludeEmptyString: true
    })
    .required(registrationTexts.welcome.errors.emptyFirstName);

export const lastNameSchema = ({ registrationTexts }) =>
  string()
    .matches(nonCharacterRegex, {
      message: registrationTexts.welcome.errors.illegalCharacter,
      excludeEmptyString: true
    })
    .required(registrationTexts.welcome.errors.emptyLastName);

export const birthIsRequiredTest = (birth) => {
  const [month, day, year] = birth.split(" ");

  const isDateValid =
    get(birth, "length", 0) === 10 &&
    !birth.includes("_") &&
    day === validateDay({ month, day, year }) &&
    isValid(new Date(invertDate(birth.replace(/\s/g, "-"))));
  return isDateValid;
};

export const isBelow130Test = (birth) =>
  getAge(invertDate(birth.replace(/\s/g, "-"))) <= MAX_AGE;

export const isAbove18Test = (birth) =>
  getAge(invertDate(birth.replace(/\s/g, "-"))) >= LEGAL_AGE;

export const birthSchema = ({ registrationTexts }) =>
  string()
    .test(
      "is-required",
      registrationTexts.welcome.errors.emptyOrInvalidBirth,
      birthIsRequiredTest
    )
    .test(
      "is-above-130",
      registrationTexts.welcome.errors.emptyOrInvalidBirth,
      isBelow130Test
    )
    .test(
      "is-above-18",
      registrationTexts.welcome.errors.birthUnder18,
      isAbove18Test
    );

export const emailSchema = ({ registrationTexts }) =>
  string()
    .required(registrationTexts.welcome.errors.emptyEmail)
    .email(registrationTexts.welcome.errors.validEmail);

export const phoneSchema = ({ registrationTexts }) =>
  string()
    .required(registrationTexts.welcome.errors.emptyPhone)
    .test(
      "is-incomplete",
      registrationTexts.welcome.errors.incompletePhone,
      (phone) => !phone.includes("_")
    );

export const passwordSchema = ({
  registrationTexts,
  capitalListItem,
  numberListItem,
  charsListItem
}) =>
  string()
    .required(registrationTexts.welcome.errors.emptyPassword)
    .test(
      "is-valid",
      registrationTexts.welcome.errors.requirementsNotMet,
      () => {
        return (
          capitalListItem.status === "SUCCESS" &&
          numberListItem.status === "SUCCESS" &&
          charsListItem.status === "SUCCESS"
        );
      }
    );

export const handleSubmitFn =
  ({
    setIsFormDirty,
    formRef,
    dispatch,
    lastStep,
    isEditing,
    isFormValid,
    registrationTexts,
    passThroughAddress,
    capitalListItem,
    setCapitalListItem,
    numberListItem,
    setNumberListItem,
    charsListItem,
    setCharsListItem,
    enableRegAutoAddress
  }) =>
  async (data) => {
    setIsFormDirty(true);
    onClickContinueBtnGtmEvent(
      `${isEditing ? "Edit " : ""}${registrationTexts.welcome.innerTitle}`
    );

    // Workaround - because when isEditing there is no password field shown so the form doesnt have it's value
    // It's pointless to pick user password at this moment as it's stored
    // and no need to re validate it so a mock password is fine.
    if (isEditing) {
      // eslint-disable-next-line no-param-reassign
      data.password = "Password123";
    }

    handlePasswordChange(
      data.password,
      capitalListItem,
      setCapitalListItem,
      numberListItem,
      setNumberListItem,
      charsListItem,
      setCharsListItem,
      true
    );

    try {
      const schema = object().shape({
        firstName: firstNameSchema({
          registrationTexts
        }),
        lastName: lastNameSchema({ registrationTexts }),
        birth: birthSchema({ registrationTexts }),
        email: emailSchema({ registrationTexts }),
        phone: phoneSchema({ registrationTexts }),
        password: passwordSchema({
          registrationTexts,
          capitalListItem,
          numberListItem,
          charsListItem
        })
      });

      await schema.validate(data, {
        abortEarly: false
      });
      // Remove all previous errors
      formRef.current.setErrors({});

      // Validation passed
      dispatch(setWelcomeFieldsRegistrationAction(data));
      dispatch(setWelcomeValidFormRegistrationAction(true));
      onFormStepSuccessGtmEvent(
        `${isEditing ? "Edit " : ""}${registrationTexts.welcome.innerTitle}`
      );

      if (passThroughAddress) {
        dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ADDRESS));
        dispatch(setPassThroughAddressAction(false));
      } else if (isEditing) {
        dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW));
      } else if (lastStep !== REGISTRATION_STEPS.WELCOME) {
        dispatch(setCurrentStepRegistrationAction(lastStep));
        dispatch(setLastStepRegistrationAction(REGISTRATION_STEPS.WELCOME));
      } else if (enableRegAutoAddress) {
        dispatch(
          setCurrentStepRegistrationAction(REGISTRATION_STEPS.AUTO_ADDRESS)
        );
      } else {
        dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.ADDRESS));
      }
    } catch (err) {
      handleSubmitCatch(
        isFormValid,
        setWelcomeValidFormRegistrationAction,
        dispatch,
        err,
        formRef,
        registrationTexts.welcome.innerTitle,
        isEditing
      );
    }
  };
